import { isPostcode } from 'platform/utilities/facilities-and-mapbox';
import constants from 'vets-json-schema/dist/constants.json';

// Lowercased US state/territory abbreviations. A lone abbreviation is an
// unsupported state-only search (#14916). Full state NAMES are excluded because
// some are also cities (e.g. "New York"); the geocoder's area check handles
// those instead, rejecting only regions too large to search (CMS #22751).
const STATE_ABBREVIATIONS = new Set(
  (constants?.states?.USA ?? []).map(({ value }) => value.toLowerCase()),
);

export const LOCATION_INPUT_STATUS = {
  VALID: 'valid',
  EMPTY: 'empty',
  INVALID: 'invalid',
};

/**
 * Synchronous guard for the location field (CMS #22751). Mapbox remains the
 * authority at submit; this only trims whitespace and rejects obviously-unusable
 * input (empty, punctuation-only, malformed numbers, bare state abbreviations)
 * before a network round-trip. Plausible cities/addresses pass through.
 *
 * @param {string} rawValue The raw location string as typed by the user.
 * @returns {string} One of LOCATION_INPUT_STATUS.
 */
export const validateLocationInput = rawValue => {
  const value = (rawValue || '').trim();

  if (!value) {
    return LOCATION_INPUT_STATUS.EMPTY;
  }

  if (isPostcode(value)) {
    return LOCATION_INPUT_STATUS.VALID;
  }

  // Numeric-only input that is not a 5-digit ZIP (e.g. "123", "1234567").
  if (/^\d+$/.test(value)) {
    return LOCATION_INPUT_STATUS.INVALID;
  }

  // Punctuation/symbol-only. Unicode-aware so names with diacritics or
  // non-Latin scripts (e.g. "Bayamón") still defer to the geocoder.
  if (!/[\p{L}\p{N}]/u.test(value)) {
    return LOCATION_INPUT_STATUS.INVALID;
  }

  // Bare state/territory abbreviation — unsupported state-only search (see the
  // STATE_ABBREVIATIONS note above).
  if (STATE_ABBREVIATIONS.has(value.toLowerCase())) {
    return LOCATION_INPUT_STATUS.INVALID;
  }

  return LOCATION_INPUT_STATUS.VALID;
};
